import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getManagers } from "../../../redux/actions/managers";
import { getCompanyStructure } from "../../../redux/actions/structure";
import { sortByPtBR } from "../../../helper/sortHelper";
import "./EmployeesFilters.scss";

const EmployeesFilters = ({ filters, onChange, onReset }) => {
  const dispatch = useDispatch();
  const structure = useSelector((state) => state?.structure);
  const managers = useSelector((state) => state?.managers);

  const [nameInput, setNameInput] = useState(filters.name || "");
  const [managerSearch, setManagerSearch] = useState("");
  const [showManagerDropdown, setShowManagerDropdown] = useState(false);

  const managerInputRef = useRef(null);
  const managerDropdownRef = useRef(null);

  useEffect(() => {
    dispatch(getCompanyStructure());
  }, [dispatch]);

  useEffect(() => {
    setNameInput(filters.name || "");
  }, [filters.name]);

  useEffect(() => {
    const trimmed = nameInput.trim();
    if (trimmed === (filters.name || "")) return;
    const handle = setTimeout(() => {
      onChange({ name: trimmed || undefined });
    }, 350);
    return () => clearTimeout(handle);
  }, [nameInput, filters.name, onChange]);

  useEffect(() => {
    if (!showManagerDropdown) return;
    const trimmed = managerSearch.trim();
    const params = trimmed ? { name: trimmed } : {};
    const handle = setTimeout(() => dispatch(getManagers(params)), 250);
    return () => clearTimeout(handle);
  }, [managerSearch, showManagerDropdown, dispatch]);

  useEffect(() => {
    if (!showManagerDropdown) return;
    const handleClickOutside = (event) => {
      if (
        managerInputRef.current &&
        !managerInputRef.current.contains(event.target) &&
        managerDropdownRef.current &&
        !managerDropdownRef.current.contains(event.target)
      ) {
        setShowManagerDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showManagerDropdown]);

  const units = useMemo(() => {
    if (!Array.isArray(structure?.data)) return [];
    return sortByPtBR(structure.data.map((unit) => unit.name).filter(Boolean));
  }, [structure?.data]);

  const rolesForSelectedUnit = useMemo(() => {
    if (!Array.isArray(structure?.data)) return [];
    if (!filters.unit) {
      const all = new Set();
      structure.data.forEach((unit) => (unit.roles || []).forEach((r) => all.add(r)));
      return sortByPtBR(Array.from(all));
    }
    const found = structure.data.find((unit) => unit.name === filters.unit);
    return sortByPtBR(found?.roles || []);
  }, [structure?.data, filters.unit]);

  const managerOptions = useMemo(() => {
    if (!Array.isArray(managers?.data?.items)) return [];
    return managers.data.items
      .map((item) => {
        const m = item?.manager;
        if (!m) return null;
        const display = m.name && m.email ? `${m.name} - ${m.email}` : m.name || m.email || String(m.id);
        return { id: m.id, display };
      })
      .filter(Boolean);
  }, [managers?.data?.items]);

  const handleUnitChange = (event) => {
    const value = event.target.value;
    onChange({ unit: value || undefined, role: undefined });
  };

  const handleRoleChange = (event) => {
    const value = event.target.value;
    onChange({ role: value || undefined });
  };

  const handleSelectManager = (manager) => {
    setShowManagerDropdown(false);
    setManagerSearch("");
    onChange({ managerId: manager.id, managerDisplay: manager.display });
  };

  const handleClearManager = () => {
    onChange({ managerId: undefined, managerDisplay: undefined });
    setManagerSearch("");
  };

  const hasActiveFilter = Boolean(
    filters.name || filters.unit || filters.role || filters.managerId
  );

  return (
    <div className="EmployeesFilters">
      <div className="filters-grid">
        <div className="filter-field">
          <label>Nome</label>
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
          />
        </div>

        <div className="filter-field">
          <label>Área</label>
          <div className="select-wrapper">
            <select value={filters.unit || ""} onChange={handleUnitChange}>
              <option value="">Todas as áreas</option>
              {units.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
            <span className="weui--arrow-outlined" />
          </div>
        </div>

        <div className="filter-field">
          <label>Cargo</label>
          <div className="select-wrapper">
            <select value={filters.role || ""} onChange={handleRoleChange}>
              <option value="">Todos os cargos</option>
              {rolesForSelectedUnit.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
            <span className="weui--arrow-outlined" />
          </div>
        </div>

        <div className="filter-field manager-field">
          <label>Gestor</label>
          <div className="manager-control" ref={managerInputRef}>
            {filters.managerId ? (
              <div className="manager-chip">
                <span className="manager-chip-text">{filters.managerDisplay}</span>
                <button
                  type="button"
                  className="manager-chip-clear"
                  onClick={handleClearManager}
                  aria-label="Limpar gestor"
                >
                  ×
                </button>
              </div>
            ) : (
              <input
                type="text"
                placeholder="Buscar gestor..."
                value={managerSearch}
                onFocus={() => setShowManagerDropdown(true)}
                onChange={(e) => {
                  setManagerSearch(e.target.value);
                  setShowManagerDropdown(true);
                }}
                autoComplete="off"
              />
            )}
            {showManagerDropdown && !filters.managerId && (
              <div className="manager-dropdown" ref={managerDropdownRef}>
                {managerOptions.length === 0 ? (
                  <div className="manager-empty">Nenhum gestor encontrado</div>
                ) : (
                  managerOptions.map((option) => (
                    <div
                      key={option.id}
                      className="manager-option"
                      onClick={() => handleSelectManager(option)}
                    >
                      {option.display}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          className="filters-reset"
          onClick={onReset}
          disabled={!hasActiveFilter}
        >
          Limpar filtros
        </button>
      </div>
    </div>
  );
};

export default EmployeesFilters;
